import { GridRegistrationStatusEnum, GridRegistrationTypeEnum } from './model/grid-registration.enum';
import { StorageModel, StorageOperationType } from './model/storage.model';

/**
 * Prozess-Entscheidungen zur Netzanmeldung, die ohne Formular-Komponenten auskommen.
 *
 * Bewusst getrennt von `grid-registration.utils.ts`: Diese Datei wird auch aus dem
 * Inbetriebnahme-Modul (Pipes, Abschluss-Prüfungen) heraus benutzt, und
 * `grid-registration.utils.ts` zieht die Formular-Komponenten mit herein.
 */

/**
 * Minimaler Datensatz, der für die Prozessbestimmung reicht. Erfüllt sowohl das vollständige
 * `GridRegistrationModel` als auch die schlanke Referenz an einem Inbetriebnahme-Gerät
 * (`DeviceModel.gridRegistration`).
 */
/**
 * `GridRegistrationStatusEnum` ist ein zusammengesetztes Konstanten-Objekt (Common- plus
 * Netzanmeldungs-Status) und damit kein Enum-Typ — der Statuswert wird hier daraus abgeleitet.
 */
type GridRegistrationStatus = (typeof GridRegistrationStatusEnum)[keyof typeof GridRegistrationStatusEnum];

export interface GridRegistrationProcessInfo {
  gridRegistrationType?: GridRegistrationTypeEnum | string | null;
  storages?: { formVersion?: number | null; storageOperationType?: StorageOperationType | null }[] | null;
}

/**
 * Folgt diese Speicher-Netzanmeldung dem Inbetriebsetzungs-Prozess (Inbetriebsetzungsprotokoll
 * vor der Inbetriebnahme, analog zur Einspeiseanlage)?
 *
 * Das ist genau dann der Fall, wenn der Antrag mit dem **neuen** Speicherformular (v2) erstellt
 * wurde **und** eine Betriebsart mit Einspeisung gewählt ist. Der alte Speicherprozess (v1)
 * kennt kein Inbetriebsetzungsprotokoll: Dort ist `storages[].formVersion` `1`/leer und
 * `storageOperationType` gar nicht gesetzt.
 */
export function storagePlaceInServiceFlowEnabled(
  model: GridRegistrationProcessInfo | null | undefined
): boolean {
  if (!model || model.gridRegistrationType !== GridRegistrationTypeEnum.Storage) {
    return false;
  }
  return !!model.storages?.some(
    (storage) =>
      Number(storage?.formVersion) === 2 && StorageModel.hasProduction(storage?.storageOperationType)
  );
}

/**
 * Ist diese Netzanmeldung ein Speicher des **neuen** Speicherprozesses (v2)?
 *
 * Anders als {@link storagePlaceInServiceFlowEnabled} ist die Betriebsart hier ohne Belang: die
 * Weiche entscheidet nur, welche **Darstellung** der technischen Angaben passt (Wechselrichter und
 * Entladeleistung statt PV-Erzeugungseinheiten), nicht ob ein Inbetriebsetzungsprotokoll folgt.
 *
 * Gleiche Regel wie `storageFormVariant` für bestehende Anträge: entscheidend ist die am Antrag
 * persistierte `storages[].formVersion`, **nicht** die Prozesskonfiguration des Mandanten. Ein
 * v1-Antrag behält damit seine Darstellung, auch wenn der Mandant inzwischen auf v2 umgestellt hat.
 */
export function storageV2Process(model: GridRegistrationProcessInfo | null | undefined): boolean {
  if (!model || model.gridRegistrationType !== GridRegistrationTypeEnum.Storage) {
    return false;
  }
  return !!model.storages?.some((storage) => Number(storage?.formVersion) === 2);
}

/**
 * Wird für diese Netzanmeldung ein Inbetriebsetzungsprotokoll erwartet, bevor eine verknüpfte
 * Inbetriebnahme abgeschlossen werden darf?
 *
 * Gilt für Einspeiseanlagen und — neu — für v2-Speicher mit Einspeisung.
 */
export function placeInServiceProtocolRequired(
  model: GridRegistrationProcessInfo | null | undefined
): boolean {
  return (
    model?.gridRegistrationType === GridRegistrationTypeEnum.Feeder || storagePlaceInServiceFlowEnabled(model)
  );
}

/**
 * Status, in dem eine verknüpfte Netzanmeldung stehen muss, damit der Inbetriebnahme-Antrag
 * abgesendet bzw. abgeschlossen werden kann.
 *
 * - Einspeiseanlage und v2-Speicher **mit** Einspeisung → *Inbetriebsetzung bestätigt*
 *   (`PlacedInServiceConfirmation`), weil beide vorher ein Inbetriebsetzungsprotokoll durchlaufen.
 * - Alle übrigen Fälle (v2-Speicher ohne Einspeisung, jeder v1-Speicher, sonstige
 *   Netzanmeldungsarten) → *Freigegeben* (`Approved`).
 */
export function requiredStateForCommissioningCompletion(
  model: GridRegistrationProcessInfo | null | undefined
): GridRegistrationStatus {
  return placeInServiceProtocolRequired(model)
    ? GridRegistrationStatusEnum.PlacedInServiceConfirmation
    : GridRegistrationStatusEnum.Approved;
}

/** Menschlich lesbarer Hinweis, welcher Status fehlt — für Fehlermeldungen im Inbetriebnahme-Modul. */
export function requiredStateForCommissioningCompletionLabel(
  model: GridRegistrationProcessInfo | null | undefined
): string {
  return placeInServiceProtocolRequired(model)
    ? 'muss die Inbetriebsetzung bestätigt sein'
    : 'muss freigegeben sein';
}
